import React, { useState } from 'react';
import { ChevronLeft, Award, Calendar, FileText, Truck, User, X } from 'lucide-react';

export default function MaterialDetailPage({ material, base, onBack, onSell }) {
  const [showModeModal, setShowModeModal] = useState(false);
  
  const isOpenDemand = material.demandType === 'open';
  const demandTypeLabel = isOpenDemand ? '敞口需求' : '闭口需求';
  const demandTypeColor = isOpenDemand ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600';
  
  const getDeliveryTimeDisplay = () => {
    if (isOpenDemand) {
      return '半年内有效';
    } else {
      return `截止 ${material.deliveryDeadline}`;
    }
  };
  
  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack} className="p-1 -ml-2">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">物料详情</span>
      </div>

      <div className="px-4 py-4">
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200 mb-4">
          <div className="h-56 bg-gradient-to-br from-slate-200 to-slate-300 relative overflow-hidden">
            <img src={material.image} alt={material.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </div>

          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-xl font-bold text-slate-800">{material.name}</h2>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${demandTypeColor}`}>
                    {demandTypeLabel}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>所属基地:</span>
                  <span className="text-slate-700 font-medium">{base?.name || '马钢诚兴基地'}</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 mb-1">最高采购价（含奖励）</div>
                  <div className="text-3xl font-bold text-red-600">
                    {material.maxPrice}
                    <span className="text-base font-normal text-slate-500 ml-1">元/吨</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 bg-amber-100 px-3 py-2 rounded-lg">
                  <Award size={16} className="text-amber-500" />
                  <div className="text-right">
                    <div className="text-xs text-amber-600">限时奖励</div>
                    <div className="text-lg font-bold text-amber-600">+{material.reward}元</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">基本信息</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Truck size={20} className="text-blue-500" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-slate-500 mb-1">收购量</div>
                <div className="text-base font-bold text-slate-800">
                  {material.fixedWeight ? `${material.fixedWeight} 吨（固定）` : `${material.estimateQty} 吨`}
                </div>
                {!material.fixedWeight && (
                  <div className="text-xs text-slate-500 mt-1">预估收购量，实际以过磅为准</div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Calendar size={20} className="text-blue-500" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-slate-500 mb-1">送货时间</div>
                <div className="text-base font-bold text-slate-800">{getDeliveryTimeDisplay()}</div>
                <div className="text-xs text-slate-500 mt-1">周一至周日 8:00-18:00，节假日正常收货</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <FileText size={20} className="text-blue-500" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-slate-500 mb-1">质检标准</div>
                <div className="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-lg p-3">
                  {material.spec}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">收购说明</h3>
          </div>

          <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 shrink-0" />
              <div>物料需符合国家相关质量标准，不得混入杂质</div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 shrink-0" />
              <div>送货前请提前预约或联系基地确认收货时间</div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 shrink-0" />
              <div>结算方式：货到验收合格后当日结算</div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 shrink-0" />
              <div>如有疑问，请拨打基地客服电话咨询</div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white/95 backdrop-blur-sm border-t border-slate-200 px-4 py-3 pointer-events-auto">
          <button
            onClick={() => setShowModeModal(true)}
            className="w-full py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
          >
            我要卖
          </button>
        </div>
      </div>

      {showModeModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center">
          <div className="bg-white rounded-t-3xl w-full max-w-md overflow-hidden shadow-2xl animate-slide-up">
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 mb-0.5 font-medium">选择交易模式</div>
                  <div className="text-lg font-bold text-slate-800">您要如何交易？</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowModeModal(false)}
                  className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-300 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-3">
              <button
                onClick={() => {
                  setShowModeModal(false);
                  onSell(material, 'self');
                }}
                className="w-full p-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <User size={24} className="text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-lg">自己卖</div>
                    <div className="text-sm text-white/80 mt-0.5">使用自己的账户进行交易</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowModeModal(false);
                  onSell(material, 'other');
                }}
                className="w-full p-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl text-white shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <User size={24} className="text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-lg">帮别人卖</div>
                    <div className="text-sm text-white/80 mt-0.5">代他人进行废钢交易</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
